import {
    getGridDateOperators,
    getGridNumericOperators,
    getGridSingleSelectOperators,
    getGridStringOperators,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColDef,
} from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';
import { RoadSection } from '../../../core/domain/Reference';
import { ViewSectionOnMapButton } from '../../network-map/ViewSectionOnMapButton';
import { ValueTypeToFilter } from '../../session/list-sessions/getDataGridColumns';
import ReferenceStringDisplay from './ReferenceStringDisplay';

const EMPTY_VALUE = '';

const getFilter = (type: ValueTypeToFilter) => {
    switch (type) {
        case 'string':
            return getGridStringOperators().filter(({ value }) => value !== 'isAnyOf');
        case 'direction':
            return getGridSingleSelectOperators().filter(({ value }) => value === 'is');
        case 'number':
            return getGridNumericOperators().filter(({ value }) => ['>', '<'].includes(value));
        default:
            return getGridDateOperators().filter(({ value }) => ['onOrAfter', 'before', 'is', 'not'].includes(value));
    }
};

export const getDataGridColumns = (t: TFunction, sortable: boolean): GridColDef[] => {
    const baseColDef = (align: GridColDef['align'] = 'right'): Partial<GridColDef> => ({
        align,
        headerAlign: 'center',
        cellClassName: 'light',
        sortable,
        filterable: true,
        hideable: true,
    });

    return [
        { ...GRID_CHECKBOX_SELECTION_COL_DEF, disableReorder: true, hideable: false },
        {
            ...baseColDef('left'),
            cellClassName: 'dark',
            display: 'flex',
            field: 'roadName',
            filterOperators: getFilter('string'),
            flex: 3,
            headerName: t('reference.roadSection.roadName'),
            minWidth: 100,
            renderCell: ({ row }) => ReferenceStringDisplay({ data: row.roadName }),
        },
        {
            ...baseColDef(),
            field: 'name',
            headerName: t('reference.roadSection.name'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.name || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, name: value }),
            renderCell: ({ row }) => row.name || '',
        },
        {
            ...baseColDef(),
            field: 'gestionary',
            headerName: t('reference.roadSection.gestionary'),
            minWidth: 150,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.gestionary || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, gestionary: value }),
            renderCell: ({ row }) => row.gestionary || '',
        },
        {
            ...baseColDef(),
            field: 'cumulStart',
            headerName: t('reference.roadSection.start'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            renderCell: ({ row }) => `${row.cumulStart.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'cumulEnd',
            headerName: t('reference.roadSection.end'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            renderCell: ({ row }) => `${row.cumulEnd.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'length',
            headerName: t('reference.roadSection.length'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            valueGetter: (_, row) => Math.abs(row.cumulEnd - row.cumulStart),
            renderCell: ({ value }) => `${value.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'prd',
            headerName: t('reference.roadSection.prd'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.prd || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, prd: value }),
            renderCell: ({ row }) => row.prd || '',
        },
        {
            ...baseColDef(),
            field: 'abd',
            headerName: t('reference.roadSection.abd'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'number',
            valueGetter: (_, row) => row.abd ?? EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, abd: value }),
            renderCell: ({ row }) => row.abd ?? null,
        },
        {
            ...baseColDef(),
            field: 'prf',
            headerName: t('reference.roadSection.prf'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.prf || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, prf: value }),
            renderCell: ({ row }) => row.prf || '',
        },
        {
            ...baseColDef(),
            field: 'abf',
            headerName: t('reference.roadSection.abf'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'number',
            valueGetter: (_, row) => row.abf || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, abf: value }),
            preProcessEditCellProps: (params) => {
                const hasError = Number.isNaN(Number(params.props.value));

                return { ...params.props, error: hasError };
            },
            renderCell: ({ row }) => row.abf || null,
        },
        {
            ...baseColDef(),
            field: 'category',
            headerName: t('reference.roadSection.category'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.category || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, category: value }),
            renderCell: ({ row }) => row.category || '',
        },
        {
            ...baseColDef(),
            field: 'trucks',
            headerName: t('reference.roadSection.trucks'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.trucks || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, trucks: value }),
            renderCell: ({ row }) => row.trucks || '',
        },
        {
            ...baseColDef(),
            field: 'urban',
            headerName: t('reference.roadSection.urban'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.urban || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, urban: value }),
            renderCell: ({ row }) => row.urban || '',
        },
        {
            ...baseColDef(),
            field: 'roadType',
            headerName: t('reference.roadSection.roadType'),
            minWidth: 150,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.roadType || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, roadType: value }),
            renderCell: ({ row }) => row.roadType || '',
        },
        {
            ...baseColDef(),
            field: 'year',
            headerName: t('reference.roadSection.year'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'number',
            valueGetter: (_, row) => row.year || EMPTY_VALUE,
            valueSetter: (value, row) => ({ ...row, year: value }),
            preProcessEditCellProps: (params) => {
                const hasError = Number.isNaN(Number(params.props.value));

                return { ...params.props, error: hasError };
            },
            renderCell: ({ row }) => row.year || '',
        },
        {
            ...baseColDef(),
            field: 'width',
            headerName: t('reference.roadSection.width'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'number',
            valueGetter: (_, row) => row.width || EMPTY_VALUE,
            valueSetter: (value, row) => {
                const parsedValue = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);

                return { ...row, width: Number.isNaN(parsedValue) ? null : parsedValue };
            },
            preProcessEditCellProps: (params) => {
                const raw = params.props.value;
                const normalized = typeof raw === 'string' ? raw.replace(',', '.') : raw;
                const hasError = Number.isNaN(Number(normalized));

                return { ...params.props, error: hasError };
            },
            renderCell: ({ row }) =>
                row.width?.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) ?? '',
        },
        {
            disableColumnMenu: true,
            disableReorder: true,
            field: 'actions',
            hideable: false,
            renderCell: ({ row }: { row: RoadSection }) => ViewSectionOnMapButton({ line: row.line }),
            resizable: false,
            type: 'actions',
            width: 60,
        },
    ];
};
